// How rare a roll is: the chance a fresh catch scores at least a given rating.
import { CONFIG } from './config.ts';
import { statWeights } from './rating.ts';

/**
 * Chance a fresh catch rolls at least `x` (before the ascension bonus). Exact:
 * the weighted talent share is built by convolving the six 0-31 rolls on a fine
 * grid (a perfect 31 in every stat lands exactly on the top bin), quality uses
 * its closed form, bloodline and aura their odds. Random sampling missed the
 * very top, so near-perfect rolls used to read as impossible.
 */
const TALENT_BINS = 6200;
const talentDist = new Map<string, Float64Array>();
/** Probability of each talent bin; bin k is a weighted share of k / (length - 1). */
function talentDistOf(speciesId: string): Float64Array {
  let d = talentDist.get(speciesId);
  if (d) return d;
  const w = statWeights(speciesId);
  const steps = w.map((x) => Math.round(x * TALENT_BINS));
  const top = steps.reduce((a, b) => a + b, 0);
  d = new Float64Array(top + 1); d[0] = 1;
  for (let i = 0; i < 6; i++) {
    if (!steps[i]) continue;
    const next = new Float64Array(top + 1);
    for (let b = 0; b <= top; b++) {
      if (!d[b]) continue;
      for (let v = 0; v <= 31; v++) next[Math.min(top, b + Math.round((v / 31) * steps[i]))] += d[b] / 32;
    }
    d = next;
  }
  talentDist.set(speciesId, d);
  return d;
}

/** Quality is stored to 0.001, so the top value is reached a little below the true maximum. */
const QUALITY_STEP = 0.0005 / (CONFIG.quality.max - CONFIG.quality.min);

/** auraOneIn: the Aura odds the catch rolled with (an Aura Charm halves them, shared/auracharm.ts). */
export function oddsAtLeast(speciesId: string, x: number, auraOneIn: number = CONFIG.auraCatchOneIn): number {
  const d = talentDistOf(speciesId);
  const P = CONFIG.ratingPoints, aura = 1 / auraOneIn;
  const plain = (y: number) => {
    let p = 0;
    CONFIG.bloodline.forEach((b, bi) => {
      let acc = 0;
      for (let k = 0; k < d.length; k++) if (d[k]) acc += d[k] * qualityAtLeast((y - 0.005 - CONFIG.bloodlineRating[bi] - (P.talent * k) / (d.length - 1)) / P.quality - QUALITY_STEP);
      p += (b.pct / 100) * acc;
    });
    return p;
  };
  return (1 - aura) * plain(x) + aura * plain(x - P.aura);
}

/** P(quality share >= t) when quality is the mean of CONFIG.quality.draws uniform draws (Irwin-Hall). */
function qualityAtLeast(t: number): number {
  if (t <= 0) return 1;
  if (t >= 1) return 0;
  // The distribution is symmetric; summing the short side keeps tiny tails exact.
  return t > 0.5 ? irwinHallCdf(1 - t) : 1 - irwinHallCdf(t);
}
/** P(mean of n uniform draws <= t). */
function irwinHallCdf(t: number): number {
  const n = CONFIG.quality.draws, sum = t * n;
  let cdf = 0, f = 1;
  for (let i = 1; i <= n; i++) f *= i;
  for (let k = 0; k <= Math.floor(sum); k++) cdf += (k % 2 ? -1 : 1) * binom(n, k) * (sum - k) ** n;
  return cdf / f;
}
function binom(n: number, k: number) { let r = 1; for (let i = 1; i <= k; i++) r = (r * (n - k + i)) / i; return r; }
